
import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Modal from '../../../components/base/Modal';

export default function PasswordSettings() {
  const { user, changePassword } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validaciones
    if (formData.newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('La nueva contraseña debe ser diferente a la actual');
      setLoading(false);
      return;
    }

    try {
      const result = await changePassword(formData.currentPassword, formData.newPassword);
      
      if (result.success) {
        setMessage('Contraseña actualizada correctamente');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowModal(false);
          setMessage('');
        }, 2000);
      } else {
        setError(result.error || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      setError('Error de conexión');
    }

    setLoading(false);
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getStrengthColor = (strength: number) => {
    if (strength <= 2) return 'bg-red-500';
    if (strength <= 3) return 'bg-yellow-500';
    if (strength <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = (strength: number) => {
    if (strength <= 2) return 'Débil';
    if (strength <= 3) return 'Regular';
    if (strength <= 4) return 'Buena';
    return 'Muy fuerte';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Configuración de Seguridad</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Información del Usuario */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Información de la Cuenta</h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Rol</p>
                  <p className="text-sm text-gray-500">
                    {user?.role === 'admin' ? 'Administrador Principal' : 'Operador'}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.role === 'admin' 
                    ? 'bg-purple-100 text-purple-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {user?.role === 'admin' ? 'Admin' : 'Operador'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cambio de Contraseña */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Seguridad de la Cuenta</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-shield-line text-yellow-600 mr-3"></i>
                <div>
                  <p className="text-sm font-medium text-yellow-800">Contraseña</p>
                  <p className="text-xs text-yellow-600">Última actualización: Configuración inicial</p>
                </div>
              </div>
              <Button
                onClick={() => setShowModal(true)}
                size="sm"
                variant="outline"
              >
                <i className="ri-key-line mr-2"></i>
                Cambiar
              </Button>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900">Recomendaciones de Seguridad</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  Usar al menos 8 caracteres
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  Incluir mayúsculas y minúsculas
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  Agregar números y símbolos
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  Cambiar periódicamente
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Actividad Reciente */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              <span className="text-sm text-gray-600">Inicio de sesión exitoso</span>
            </div>
            <span className="text-xs text-gray-400">Hoy</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              <span className="text-sm text-gray-600">Acceso al panel administrativo</span>
            </div>
            <span className="text-xs text-gray-400">Hoy</span>
          </div>
        </div>
      </div>

      {/* Modal de Cambio de Contraseña */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setError('');
          setMessage('');
        }}
        title="Cambiar Contraseña"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <Input
              label="Contraseña Actual"
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
              required
              icon="ri-lock-line"
              placeholder="Ingrese su contraseña actual"
            />

            <Input
              label="Nueva Contraseña"
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
              required
              icon="ri-key-line"
              placeholder="Ingrese la nueva contraseña"
            />

            {formData.newPassword && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fortaleza de la contraseña:</span>
                  <span className={`font-medium ${
                    getPasswordStrength(formData.newPassword) <= 2 ? 'text-red-600' :
                    getPasswordStrength(formData.newPassword) <= 3 ? 'text-yellow-600' :
                    getPasswordStrength(formData.newPassword) <= 4 ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {getStrengthText(getPasswordStrength(formData.newPassword))}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(getPasswordStrength(formData.newPassword))}`}
                    style={{ width: `${(getPasswordStrength(formData.newPassword) / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            <Input
              label="Confirmar Nueva Contraseña"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              required
              icon="ri-check-line"
              placeholder="Confirme la nueva contraseña"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-500 mr-2"></i>
                <p className="text-green-600 text-sm">{message}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setShowModal(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              loading={loading}
            >
              <i className="ri-save-line mr-2"></i>
              Cambiar Contraseña
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
